import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, Mail, Lock, User, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuthStore, useUIStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isLogin = location.pathname === '/login';
  
  const { login, register, isLoading } = useAuthStore();
  const { language } = useUIStore();
  const { t } = useTranslation(language);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success(t('success'));
        navigate('/onboarding');
      } else {
        await register(formData.email, formData.password, formData.name, language);
        toast.success(t('success'));
        navigate('/onboarding');
      }
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-6">
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/')}
        className="absolute top-6 left-6"
        data-testid="auth-back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center">
            <Sparkles className="h-7 w-7 text-primary-foreground" />
          </div>
          <span className="font-heading font-bold text-2xl">OSLO One</span>
        </div>

        {/* Auth Card */}
        <div className="glass rounded-2xl p-8">
          <h1 className="font-heading text-2xl font-bold text-center mb-6">
            {isLogin ? t('login_title') : t('register_title')}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name">{t('name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="pl-10 bg-secondary/50 border-white/10 focus:border-primary"
                    required={!isLogin}
                    data-testid="auth-name-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="pl-10 bg-secondary/50 border-white/10 focus:border-primary"
                  required
                  data-testid="auth-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 bg-secondary/50 border-white/10 focus:border-primary"
                  required
                  minLength={6}
                  data-testid="auth-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full glow-primary py-6 text-lg"
              disabled={isLoading}
              data-testid="auth-submit-btn"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isLogin ? (
                t('login')
              ) : (
                t('register')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground text-sm">
              {isLogin ? t('no_account') : t('have_account')}{' '}
              <button
                onClick={() => navigate(isLogin ? '/register' : '/login')}
                className="text-primary hover:underline font-medium"
                data-testid="auth-switch-btn"
              >
                {isLogin ? t('register') : t('login')}
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
